import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { ibkrService } from '../../services/ibkrService';

interface OpenOrder {
  order_id: number;
  symbol: string;
  sec_type: string;
  expiry: string;
  strike: number;
  right: string;
  action: string;
  quantity: number;
  order_type: string;
  limit_price: number;
  aux_price: number;
  tif: string;
  status: string;
  outside_rth: boolean;
}

function formatTyp(order: OpenOrder): string {
  if (order.sec_type === 'OPT') {
    const expiry = order.expiry
      ? `${order.expiry.slice(0, 4)}-${order.expiry.slice(4, 6)}-${order.expiry.slice(6, 8)}`
      : '';
    const right = order.right === 'C' ? 'C' : order.right === 'P' ? 'P' : order.right;
    return `${order.symbol} ${expiry} ${order.strike}${right}`;
  }
  if (order.sec_type === 'FUT') return 'Future';
  return 'Aktie';
}

function formatLimit(order: OpenOrder): string {
  if (order.order_type === 'MKT') return '-';
  return `$${order.limit_price.toFixed(2)}`;
}

const IBKROrders: React.FC = () => {
  const [orders, setOrders] = useState<OpenOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const fetchOrders = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getOpenOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der offenen Orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleCancel = async (orderId: number) => {
    setCancellingId(orderId);
    try {
      await ibkrService.cancelOrder(orderId);
      await fetchOrders();
    } catch (err: any) {
      setError(err.message || 'Stornierung fehlgeschlagen');
    } finally {
      setCancellingId(null);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchOrders();
  };

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Orderbuch ({orders.length})
        </Typography>
        <Tooltip title="Aktualisieren">
          <span>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {orders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Keine offenen Orders.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Aktion</TableCell>
                <TableCell align="right">Menge</TableCell>
                <TableCell>Order-Typ</TableCell>
                <TableCell align="right">Limit</TableCell>
                <TableCell>TIF</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>RTH</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => {
                const isSubmitted = ['Submitted', 'Filled', 'PreSubmitted'].includes(order.status);
                return (
                  <TableRow key={order.order_id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{order.symbol}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {formatTyp(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.action}
                        color={order.action === 'BUY' ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{order.quantity}</TableCell>
                    <TableCell>{order.order_type}</TableCell>
                    <TableCell align="right">{formatLimit(order)}</TableCell>
                    <TableCell>{order.tif}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={isSubmitted ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={order.outside_rth ? 'success.main' : 'text.secondary'}>
                        {order.outside_rth ? 'Ja' : 'Nein'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleCancel(order.order_id)}
                        disabled={cancellingId === order.order_id}
                      >
                        Stornieren
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default IBKROrders;
